(() => {
  const selectors = {
    drawer: 'header-drawer',
    details: '#Details-menu-drawer-container',
    menu: '[data-mobile-menu]',
    close: '[data-mobile-menu-close]',
    search: '[data-mobile-search-trigger]',
    tab: '[data-mobile-menu-tab]',
    panel: '[data-mobile-menu-panel]',
    subpanelOpen: '[data-mobile-subpanel-open]',
    subpanelClose: '[data-mobile-subpanel-close]',
    subpanel: '[data-mobile-subpanel]',
  };

  class MobileHeaderMenu {
    constructor(drawer) {
      this.drawer = drawer;
      this.details = drawer.querySelector(selectors.details);
      this.menu = drawer.querySelector(selectors.menu);
      this.summary = this.details?.querySelector(':scope > summary');

      if (!this.details || !this.menu || !this.summary) return;

      this.tabs = Array.from(this.menu.querySelectorAll(selectors.tab));
      this.panels = Array.from(this.menu.querySelectorAll(selectors.panel));
      this.subpanels = Array.from(this.menu.querySelectorAll(selectors.subpanel));
      this.body = this.menu.querySelector('.plg-mobile-menu__body');
      this.imagesPreloaded = false;

      this.bindEvents();
      this.observeDrawer();

      if (this.details.hasAttribute('open')) {
        this.preloadMenuImages();
        this.reset();
      }
    }

    bindEvents() {
      this.menu.querySelectorAll(selectors.close).forEach((trigger) => {
        trigger.addEventListener('click', (event) => {
          event.preventDefault();
          this.summary.click();
        });
      });

      this.menu.querySelectorAll(selectors.search).forEach((trigger) => {
        trigger.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.openSearchModal();
        });
      });

      this.tabs.forEach((tab) => {
        tab.addEventListener('click', (event) => {
          event.preventDefault();
          this.activateTab(tab);
        });
      });

      this.menu.querySelectorAll(selectors.subpanelOpen).forEach((trigger) => {
        trigger.addEventListener('click', (event) => {
          event.preventDefault();
          this.openSubpanel(trigger);
        });
      });

      this.menu.querySelectorAll(selectors.subpanelClose).forEach((trigger) => {
        trigger.addEventListener('click', (event) => {
          event.preventDefault();
          this.closeSubpanels();
        });
      });
    }

    observeDrawer() {
      const observer = new MutationObserver(() => {
        if (this.details.hasAttribute('open')) {
          this.preloadMenuImages();
          this.reset();
        }
      });

      observer.observe(this.details, {
        attributes: true,
        attributeFilter: ['open'],
      });
    }

    activateTab(activeTab) {
      const panelId = activeTab.getAttribute('aria-controls');

      this.tabs.forEach((tab) => {
        const isActive = tab === activeTab;
        tab.classList.toggle('is-active', isActive);
        tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });

      this.panels.forEach((panel) => {
        const isActive = panel.id === panelId;
        panel.classList.toggle('is-active', isActive);
        panel.hidden = !isActive;

        if (isActive) {
          this.preloadPanelImages(panel, 'high');
        }
      });

      this.closeSubpanels();
    }

    preloadPanelImages(panel, priority = 'low') {
      if (!panel) return;

      panel.querySelectorAll('[data-mobile-menu-image]').forEach((image) => {
        image.loading = 'eager';
        if ('fetchPriority' in image) image.fetchPriority = priority;

        if (image.complete) return;

        const preloadImage = new Image();
        preloadImage.decoding = 'async';
        if ('fetchPriority' in preloadImage) preloadImage.fetchPriority = priority;
        preloadImage.sizes = image.sizes;
        preloadImage.srcset = image.srcset;
        preloadImage.src = image.currentSrc || image.src;
      });
    }

    preloadMenuImages() {
      const activePanel = this.panels.find((panel) => panel.classList.contains('is-active')) || this.panels[0];

      this.preloadPanelImages(activePanel, 'high');

      if (this.imagesPreloaded) return;
      this.imagesPreloaded = true;

      window.requestIdleCallback
        ? window.requestIdleCallback(() => {
            this.panels.forEach((panel) => {
              if (panel !== activePanel) this.preloadPanelImages(panel, 'low');
            });
          }, { timeout: 800 })
        : window.setTimeout(() => {
            this.panels.forEach((panel) => {
              if (panel !== activePanel) this.preloadPanelImages(panel, 'low');
            });
          }, 120);
    }

    openSubpanel(trigger) {
      const id = trigger.getAttribute('aria-controls');
      const panel = id ? this.menu.querySelector(`#${CSS.escape(id)}`) : null;

      if (!panel) return;

      this.closeSubpanels();
      this.menu.classList.add('is-subpanel-open');
      if (this.body) this.body.scrollTop = 0;
      panel.scrollTop = 0;
      panel.classList.add('is-active');
      panel.setAttribute('aria-hidden', 'false');
      trigger.setAttribute('aria-expanded', 'true');
    }

    closeSubpanels() {
      this.menu.classList.remove('is-subpanel-open');

      this.subpanels.forEach((panel) => {
        panel.classList.remove('is-active');
        panel.setAttribute('aria-hidden', 'true');
      });

      this.menu.querySelectorAll(selectors.subpanelOpen).forEach((trigger) => {
        trigger.setAttribute('aria-expanded', 'false');
      });
    }

    openSearchModal() {
      const searchModal = document.querySelector('details-modal.header__search');
      const searchSummary = searchModal?.querySelector('summary');

      if (!searchModal || !searchSummary) return;

      window.setTimeout(() => {
        if (typeof searchModal.open === 'function') {
          searchModal.open({ target: searchSummary });
        } else {
          searchSummary.click();
        }
      }, 0);
    }

    reset() {
      const firstTab = this.tabs[0];

      if (firstTab) {
        this.activateTab(firstTab);
      }

      this.closeSubpanels();
    }
  }

  const initMobileMenus = () => {
    document.querySelectorAll(selectors.drawer).forEach((drawer) => {
      if (drawer.dataset.mobileMenuInitialized === 'true') return;
      drawer.dataset.mobileMenuInitialized = 'true';
      new MobileHeaderMenu(drawer);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initMobileMenus);
  } else {
    initMobileMenus();
  }
})();
